/**
 * C: Dispense-tracking ledger (read-only).
 *
 * A unified, filterable stream of every stock movement — issues OUT (to rooms,
 * by people) and receipts IN (from POs) — sourced from the
 * inventory_movement_ledger view (see migration
 * 20260618012000_inventory_movement_ledger_view.sql). Tabs scope the view By
 * Room / By Person / By Item / Full Ledger; filters narrow by date range, room,
 * person, item and movement type; and the current rows can be exported to CSV.
 *
 * Strictly read-only: it never mutates stock — it just reflects the audit
 * trails written by the confirm/receive RPCs.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowDownToLine, ArrowUpFromLine, Download, RefreshCw } from 'lucide-react';
import {
  inventoryService,
  type InventoryMovementLedgerRow,
} from '@/services/inventoryService';

type LedgerScope = 'room' | 'person' | 'item' | 'full';
type MovementFilter = 'all' | 'in' | 'out';

const formatDateTime = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-SG', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const csvCell = (value: unknown): string => {
  const str = value == null ? '' : String(value);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const DispenseLedgerView: React.FC = () => {
  const [rows, setRows] = useState<InventoryMovementLedgerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [scope, setScope] = useState<LedgerScope>('full');
  const [movementFilter, setMovementFilter] = useState<MovementFilter>('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [roomFilter, setRoomFilter] = useState('all');
  const [personFilter, setPersonFilter] = useState('all');
  const [itemFilter, setItemFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await inventoryService.getMovementLedger();
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the movement ledger.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const roomOptions = useMemo(
    () =>
      Array.from(
        new Map(rows.filter((r) => r.room_id).map((r) => [r.room_id as string, r.room_name || r.room_id])).entries()
      ).sort((a, b) => String(a[1]).localeCompare(String(b[1]))),
    [rows]
  );
  const personOptions = useMemo(
    () =>
      Array.from(
        new Map(
          rows.filter((r) => r.person_user_id).map((r) => [r.person_user_id as string, r.person_name || r.person_user_id])
        ).entries()
      ).sort((a, b) => String(a[1]).localeCompare(String(b[1]))),
    [rows]
  );
  const itemOptions = useMemo(
    () =>
      Array.from(
        new Map(rows.filter((r) => r.item_id).map((r) => [r.item_id as string, r.item_name || r.item_id])).entries()
      ).sort((a, b) => String(a[1]).localeCompare(String(b[1]))),
    [rows]
  );

  const filtered = useMemo(() => {
    const fromTs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : null;
    const toTs = toDate ? new Date(`${toDate}T23:59:59`).getTime() : null;

    return rows.filter((r) => {
      if (movementFilter !== 'all' && r.direction !== movementFilter) return false;
      if (roomFilter !== 'all' && r.room_id !== roomFilter) return false;
      if (personFilter !== 'all' && r.person_user_id !== personFilter) return false;
      if (itemFilter !== 'all' && r.item_id !== itemFilter) return false;
      // Scope tabs hide rows that lack the scoping dimension.
      if (scope === 'room' && !r.room_id) return false;
      if (scope === 'person' && !r.person_user_id) return false;
      const ts = new Date(r.occurred_at).getTime();
      if (fromTs != null && ts < fromTs) return false;
      if (toTs != null && ts > toTs) return false;
      return true;
    });
  }, [rows, movementFilter, roomFilter, personFilter, itemFilter, scope, fromDate, toDate]);

  const exportCsv = () => {
    const header = [
      'When', 'Item', 'Code', 'Qty', 'Unit', 'Direction', 'Type', 'Room', 'Person', 'By whom', 'Reference', 'Balance after', 'Notes',
    ];
    const lines = filtered.map((r) =>
      [
        formatDateTime(r.occurred_at),
        r.item_name, r.item_code, r.qty, r.item_unit,
        r.direction, r.movement_type, r.room_name, r.person_name, r.actor_name,
        r.reference, r.balance_after, r.notes,
      ]
        .map(csvCell)
        .join(',')
    );
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispense-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetFilters = () => {
    setMovementFilter('all');
    setFromDate('');
    setToDate('');
    setRoomFilter('all');
    setPersonFilter('all');
    setItemFilter('all');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-slate-950">Dispense ledger</p>
          <p className="text-xs text-slate-500">
            Every confirmed stock movement — issues out and receipts in. Read-only audit trail.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-xl" onClick={() => void load()} disabled={loading}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" className="rounded-xl" onClick={exportCsv} disabled={filtered.length === 0}>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Export CSV
          </Button>
        </div>
      </div>

      <Tabs value={scope} onValueChange={(v) => setScope(v as LedgerScope)}>
        <TabsList className="grid h-auto grid-cols-4 gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-2">
          <TabsTrigger value="room" className="rounded-xl px-3 py-2">By Room</TabsTrigger>
          <TabsTrigger value="person" className="rounded-xl px-3 py-2">By Person</TabsTrigger>
          <TabsTrigger value="item" className="rounded-xl px-3 py-2">By Item</TabsTrigger>
          <TabsTrigger value="full" className="rounded-xl px-3 py-2">Full Ledger</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid gap-2 rounded-2xl border border-slate-200 bg-white p-3 sm:grid-cols-2 xl:grid-cols-6">
        <label className="flex flex-col gap-1 text-xs text-slate-600">
          From
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-600">
          To
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-600">
          Movement
          <select
            value={movementFilter}
            onChange={(e) => setMovementFilter(e.target.value as MovementFilter)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
          >
            <option value="all">All</option>
            <option value="out">Out (issued)</option>
            <option value="in">In (received)</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-600">
          Room
          <select
            value={roomFilter}
            onChange={(e) => setRoomFilter(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
          >
            <option value="all">All rooms</option>
            {roomOptions.map(([id, name]) => (
              <option key={id} value={id}>{String(name)}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-600">
          Person
          <select
            value={personFilter}
            onChange={(e) => setPersonFilter(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
          >
            <option value="all">All people</option>
            {personOptions.map(([id, name]) => (
              <option key={id} value={id}>{String(name)}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-600">
          Item
          <select
            value={itemFilter}
            onChange={(e) => setItemFilter(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
          >
            <option value="all">All items</option>
            {itemOptions.map(([id, name]) => (
              <option key={id} value={id}>{String(name)}</option>
            ))}
          </select>
        </label>
        <div className="flex items-end sm:col-span-2 xl:col-span-6">
          <Button variant="ghost" size="sm" className="rounded-xl text-slate-500" onClick={resetFilters}>
            Reset filters
          </Button>
          <span className="ml-auto self-center text-xs text-slate-500">
            {filtered.length} movement{filtered.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {error ? (
        <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead>Direction</TableHead>
                <TableHead>Room</TableHead>
                <TableHead>Person</TableHead>
                <TableHead>By whom</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead className="text-right">Balance after</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-8 text-center text-sm text-slate-500">Loading…</TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-8 text-center text-sm text-slate-500">
                    No movements match the current filters.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => (
                  <TableRow key={r.movement_id}>
                    <TableCell className="whitespace-nowrap text-xs text-slate-600">{formatDateTime(r.occurred_at)}</TableCell>
                    <TableCell>
                      <p className="font-medium text-slate-900">{r.item_name ?? 'Unknown item'}</p>
                      <p className="text-xs text-slate-500">{r.item_code ?? 'No code'}</p>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {r.qty} <span className="text-xs font-normal text-slate-500">{r.item_unit ?? ''}</span>
                    </TableCell>
                    <TableCell>
                      {r.direction === 'in' ? (
                        <Badge className="border-green-300 bg-green-50 text-green-700 hover:bg-green-50">
                          <ArrowDownToLine className="mr-1 h-3 w-3" /> In
                        </Badge>
                      ) : (
                        <Badge className="border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-50">
                          <ArrowUpFromLine className="mr-1 h-3 w-3" /> Out
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{r.room_name ?? '—'}</TableCell>
                    <TableCell className="text-sm text-slate-700">{r.person_name ?? '—'}</TableCell>
                    <TableCell className="text-sm text-slate-700">{r.actor_name ?? '—'}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.reference ?? '—'}</TableCell>
                    <TableCell className="text-right text-sm text-slate-700">{r.balance_after}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default DispenseLedgerView;
